import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UserCheck } from 'lucide-react';
import { switchProfile } from '../../redux/familySlice';

// Shown across the top whenever the login is acting as someone OTHER than the
// account holder.
//
// This is not decoration. With a profile switcher it is genuinely easy to book
// an appointment, upload a document or answer a doctor while on the wrong
// person — and in a clinic that means the wrong medical record. The bar makes
// the current identity impossible to miss and gets back in one click.
export default function ActingAsBanner() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { primary, members, activePatientId } = useSelector((s) => s.family || {});

    // Nothing to warn about when acting as the account holder.
    if (!primary || !activePatientId || activePatientId === primary.patientId) return null;

    const acting = (members || []).find((m) => m.patientId === activePatientId);
    if (!acting) return null;

    const nameOf = (p) => [p?.name, p?.surname].filter(Boolean).join(' ').trim();

    const back = async () => {
        try {
            await dispatch(switchProfile(primary.patientId)).unwrap();
            window.location.reload();
        } catch {
            // switchProfile surfaces its own failure; nothing useful to add here.
        }
    };

    return (
        <div
            data-testid="acting-as-banner"
            className="flex flex-wrap items-center gap-2 bg-amber-100 border-b border-amber-300 px-4 py-2 text-amber-900"
        >
            <UserCheck className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs font-semibold">
                {t('family.acting_as', 'You are viewing {{name}}’s account', { name: nameOf(acting) })}
            </span>
            <button
                type="button"
                data-testid="acting-as-back"
                onClick={back}
                className="ml-auto rounded-lg border border-amber-400 bg-white/70 px-2.5 py-1 text-[11px] font-semibold hover:bg-white cursor-pointer"
            >
                {t('family.back_to_primary', 'Switch back to {{name}}', { name: nameOf(primary) })}
            </button>
        </div>
    );
}
